import React, { useCallback, useEffect, useRef } from 'react';

import { toast } from '@/components/ui/toast';
import { useOAuthStore } from '@/features/auth/store';
// the store module only: the feature barrel also carries the flow's dialogs,
// and the sign-out one imports the logout routine.
import { useFlowStore } from '@/features/flow/store';
import { isSessionExpired, useWalletStore } from '@/features/wallet/store';
import { fetchOAuthMe } from '@/lib/api/auth';
import {
    exchangeAccessToken,
    fetchTradingSession,
} from '@/lib/api/tradingSession';
import type { TradingSessionStatus } from '@/lib/api/tradingSession';

import {
    TRADING_SESSION_LOCAL_EVENT,
    TRADING_SESSION_CHANNEL,
    type TradingSessionBroadcastMessage,
} from './broadcast';
import TradingSessionDialog from './components/TradingSessionDialog';
import TradingSessionWarning from './components/TradingSessionWarning';
import {
    clearLocalAuthentication,
    clearLocalOAuthTradingAuthority,
} from './logout';
import { isOAuthAuthorityRevokedByGuard } from './oauthBindingGuard';
import {
    getOAuthAuthorityRefreshDelay,
    getTradingSessionTickerDelay,
} from './sessionRuntime';
import { setTradingSessionRefresher } from './statusRefresh';
import { useTradingSessionStore } from './store';

function isSignedMethod(method: string | null): boolean {
    return method === 'eip712' || method === 'passkey';
}

function hasCorrelation(value: {
    owner?: string;
    sessionId?: string;
}): value is { owner: string; sessionId: string } {
    return (
        typeof value.owner === 'string' && typeof value.sessionId === 'string'
    );
}

function hasPartialCorrelation(value: {
    owner?: string;
    sessionId?: string;
}): boolean {
    return (
        (typeof value.owner === 'string') !==
        (typeof value.sessionId === 'string')
    );
}

function correlationMatches(
    expected: { owner: string; sessionId: string },
    candidate: { owner?: string; sessionId?: string },
): boolean {
    return (
        hasCorrelation(candidate) &&
        candidate.owner.toLowerCase() === expected.owner.toLowerCase() &&
        candidate.sessionId.toLowerCase() === expected.sessionId.toLowerCase()
    );
}

const TOKEN_EXCHANGE_RETRY_DELAYS_MS = [5_000, 15_000, 30_000] as const;

/**
 * Drops an app token minted from social authority once the server has ended
 * the social session behind it.
 *
 * Such a token carries neither owner nor sessionId: signed (EIP-712/passkey)
 * windows and legacy wallet logins both record an owner, so those are left
 * alone. Nothing can renew it: the exchange needs the social session, and that
 * is exactly what `/v2/auth/me` now says is gone (a burned refresh family, a
 * sign-out elsewhere). Kept, it held the UI half signed in for as long as the
 * token lived: the real balance on screen, the pill on Demo, the trade rail
 * "finishing sign in" through every reload. A profile that has not been read
 * yet is not a sign-out, so null leaves the token untouched.
 */
function dropOrphanedSocialAppToken(): void {
    const me = useOAuthStore.getState().me;
    if (me === null || me.authenticated) return;
    const session = useWalletStore.getState().session;
    if (
        !session ||
        typeof session.owner === 'string' ||
        typeof session.sessionId === 'string'
    ) {
        return;
    }
    console.warn(
        '[TradingSession] discarding the app token: its social session has ended',
    );
    useWalletStore.getState().clearSession();
}

/**
 * The browser wallet flow (V2) is in use. This provider keeps running then (it
 * keeps a dual user's V1 session warm for a switch back), but nothing it does
 * may surface a V1 prompt or clear the shared query cache: the V2 surface owns
 * the screen.
 */
function isBrowserWalletFlow(): boolean {
    return useFlowStore.getState().active === 'v2';
}

function signedSessionRetryKey(status: TradingSessionStatus): string | null {
    if (
        status.state !== 'active' ||
        status.method === null ||
        status.method === undefined ||
        !isSignedMethod(status.method)
    ) {
        return null;
    }
    return [
        status.method,
        status.owner?.toLowerCase() ?? '',
        status.sessionId?.toLowerCase() ?? '',
        status.issuedAtMs ?? '',
        status.expiresAtMs ?? '',
    ].join(':');
}

interface TradingSessionProviderProps {
    children: React.ReactNode;
}

const TradingSessionProvider: React.FC<TradingSessionProviderProps> = ({
    children,
}) => {
    const status = useTradingSessionStore((state) => state.status);
    const hasResolved = useTradingSessionStore((state) => state.hasResolved);
    const clockNowMs = useTradingSessionStore((state) => state.clockNowMs);
    const applyStatus = useTradingSessionStore((state) => state.applyStatus);
    const oauthSignedOut = useOAuthStore(
        (state) => state.me !== null && !state.me.authenticated,
    );
    const setLoading = useTradingSessionStore((state) => state.setLoading);
    const tick = useTradingSessionStore((state) => state.tick);
    const requestReauthentication = useTradingSessionStore(
        (state) => state.requestReauthentication,
    );
    const browserWalletFlow = useFlowStore((state) => state.active === 'v2');
    const refreshInFlight = useRef<Promise<void> | null>(null);
    // a refresh asked for while one is already running must not be dropped:
    // the running one may have sent its request BEFORE the change it is being
    // asked about (the OAuth callback landing during the boot refresh), so its
    // answer is stale by the time it arrives. one follow-up run is coalesced.
    const refreshQueued = useRef(false);
    const refreshRef = useRef<(() => Promise<void>) | null>(null);
    const mountedRef = useRef(true);
    const tokenExchangeRetryRef = useRef<{
        sessionKey: string | null;
        attempt: number;
        timer: number | null;
    }>({
        sessionKey: null,
        attempt: 0,
        timer: null,
    });

    const clearTokenExchangeRetry = useCallback(() => {
        const retry = tokenExchangeRetryRef.current;
        if (retry.timer !== null) window.clearTimeout(retry.timer);
        retry.sessionKey = null;
        retry.attempt = 0;
        retry.timer = null;
    }, []);

    const scheduleTokenExchangeRetry = useCallback(
        (nextStatus: TradingSessionStatus) => {
            const sessionKey = signedSessionRetryKey(nextStatus);
            if (sessionKey === null || !mountedRef.current) return;

            const retry = tokenExchangeRetryRef.current;
            if (retry.sessionKey !== sessionKey) {
                clearTokenExchangeRetry();
                retry.sessionKey = sessionKey;
            }
            if (retry.timer !== null) return;

            const delayMs = TOKEN_EXCHANGE_RETRY_DELAYS_MS[retry.attempt];
            if (delayMs === undefined) return;
            if (retry.attempt === 0 && !isBrowserWalletFlow()) {
                toast.warning(
                    'Your trading window is active, but secure access could not be restored. Retrying automatically.',
                );
            }
            retry.attempt += 1;
            retry.timer = window.setTimeout(() => {
                retry.timer = null;
                if (mountedRef.current) void refreshRef.current?.();
            }, delayMs);
        },
        [clearTokenExchangeRetry],
    );

    const refresh = useCallback(async (): Promise<void> => {
        if (refreshInFlight.current) {
            refreshQueued.current = true;
            return refreshInFlight.current;
        }

        const refreshPromise = (async () => {
            setLoading(true);
            const observedAtMs = Date.now();
            try {
                let oauth = useOAuthStore.getState().me;
                if (oauth?.authenticated) {
                    try {
                        oauth = await fetchOAuthMe();
                        useOAuthStore.getState().setMe(oauth);
                    } catch {
                        // OAuth profile availability must not erase a
                        // separately validated EIP-712/passkey session.
                    }
                }

                const nextStatus = await fetchTradingSession();
                if (
                    nextStatus.method === 'oauth' &&
                    // in V2 the unlink guard would clear the shared query
                    // cache under the browser wallet; it runs again on the
                    // first refresh after a switch back to V1.
                    !isBrowserWalletFlow() &&
                    (await isOAuthAuthorityRevokedByGuard(
                        oauth?.accountHash,
                        oauth?.bindingState,
                    ))
                ) {
                    clearTokenExchangeRetry();
                    clearLocalOAuthTradingAuthority(nextStatus.method);
                    return;
                }
                if (
                    nextStatus.state === 'active' &&
                    nextStatus.method !== null &&
                    nextStatus.method !== undefined
                ) {
                    const currentSession = useWalletStore.getState().session;
                    const signed = isSignedMethod(nextStatus.method);
                    if (!signed) clearTokenExchangeRetry();
                    if (signed && hasPartialCorrelation(nextStatus)) {
                        useWalletStore.getState().clearSession();
                        throw new Error(
                            'Signed trading-session status has incomplete correlation metadata.',
                        );
                    }
                    const statusCorrelation = hasCorrelation(nextStatus)
                        ? nextStatus
                        : null;
                    const mustRefresh =
                        !currentSession ||
                        isSessionExpired(currentSession, observedAtMs) ||
                        (signed
                            ? statusCorrelation
                                ? !correlationMatches(
                                      statusCorrelation,
                                      currentSession,
                                  )
                                : hasCorrelation(currentSession)
                            : hasCorrelation(currentSession));
                    if (mustRefresh) {
                        // the token is destroyed BEFORE the exchange, so every load where this is
                        // true has a window with no app token. log which condition caused it: the
                        // correlation case fires on every single load and never self-corrects.
                        console.warn(
                            '[TradingSession] discarding the stored app token before re-exchange',
                            {
                                reason: !currentSession
                                    ? 'no stored session'
                                    : isSessionExpired(
                                            currentSession,
                                            observedAtMs,
                                        )
                                      ? 'stored session expired'
                                      : 'correlation mismatch with the reported method',
                                statusMethod: nextStatus.method,
                                signedMethod: signed,
                                statusHasCorrelation:
                                    hasCorrelation(nextStatus),
                                sessionHasCorrelation: hasCorrelation(
                                    currentSession ?? {},
                                ),
                            },
                        );
                        useWalletStore.getState().clearSession();
                    }

                    // The cookie status is authoritative even when app-token
                    // exchange is temporarily unavailable. Real mode remains
                    // fail-closed because a current app token is still required.
                    applyStatus(nextStatus, observedAtMs);

                    if (mustRefresh) {
                        try {
                            const appToken = await exchangeAccessToken();
                            const invalidCorrelation =
                                hasPartialCorrelation(appToken) ||
                                (signed
                                    ? statusCorrelation
                                        ? !correlationMatches(
                                              statusCorrelation,
                                              appToken,
                                          )
                                        : hasCorrelation(appToken)
                                    : hasCorrelation(appToken));
                            if (invalidCorrelation) {
                                clearTokenExchangeRetry();
                                useWalletStore.getState().clearSession();
                                return;
                            }
                            useWalletStore.getState().saveAppSession(
                                appToken.token,
                                appToken.expiresAtMs,
                                hasCorrelation(appToken)
                                    ? {
                                          owner: appToken.owner,
                                          sessionId: appToken.sessionId,
                                      }
                                    : undefined,
                            );
                            clearTokenExchangeRetry();
                        } catch (error) {
                            // this used to be a bare catch. the exchange failing is the ONLY reason
                            // the app token stays missing for 5s+ (the retry ladder), and with no log
                            // there was nothing to diagnose from — the same silent-swallow that made
                            // the /v2/auth/me hydration bug invisible. never remove this.
                            console.error(
                                '[TradingSession] app-token exchange failed — no app token until it succeeds',
                                {
                                    method: nextStatus.method,
                                    state: nextStatus.state,
                                },
                                error,
                            );
                            useWalletStore.getState().clearSession();
                            scheduleTokenExchangeRetry(nextStatus);
                        }
                    } else {
                        clearTokenExchangeRetry();
                    }
                    return;
                }
                clearTokenExchangeRetry();
                applyStatus(nextStatus, observedAtMs);
            } catch {
                clearTokenExchangeRetry();
                // Auth-state availability is fail-closed. Keep any app token so
                // non-sensitive reads can recover, but remove trading authority.
                applyStatus(
                    {
                        state: 'none',
                        method: null,
                        issuedAtMs: null,
                        expiresAtMs: null,
                        serverTimeMs: Date.now(),
                    },
                    Date.now(),
                );
            } finally {
                setLoading(false);
            }
        })();

        const settled = refreshPromise.finally(() => {
            refreshInFlight.current = null;
            if (refreshQueued.current && mountedRef.current) {
                refreshQueued.current = false;
                void refreshRef.current?.();
            } else {
                refreshQueued.current = false;
            }
        });
        refreshInFlight.current = refreshPromise;
        void settled;
        return refreshPromise;
    }, [
        applyStatus,
        clearTokenExchangeRetry,
        scheduleTokenExchangeRetry,
        setLoading,
    ]);

    refreshRef.current = refresh;

    // non-React callers (the signer-mutation preflight) get one fresh read of
    // the status through here rather than duplicating the fetch and the
    // app-token exchange that goes with it.
    useEffect(() => {
        setTradingSessionRefresher(refresh);
        return () => setTradingSessionRefresher(null);
    }, [refresh]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            clearTokenExchangeRetry();
        };
    }, [clearTokenExchangeRetry]);

    useEffect(() => {
        const retrySessionKey = tokenExchangeRetryRef.current.sessionKey;
        const activeSessionKey = status ? signedSessionRetryKey(status) : null;
        if (retrySessionKey !== null && retrySessionKey !== activeSessionKey) {
            clearTokenExchangeRetry();
        }
    }, [clearTokenExchangeRetry, status]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    // reactive rather than inside refresh(): on a cold load the profile read
    // and the status read race, and whichever lands second has to be the one
    // that sees both answers together.
    useEffect(() => {
        if (!oauthSignedOut || !hasResolved) return;
        if (status?.state === 'active') return;
        dropOrphanedSocialAppToken();
    }, [hasResolved, oauthSignedOut, status]);

    useEffect(() => {
        const handleFocus = () => void refresh();
        const handleVisibility = () => {
            tick();
            if (document.visibilityState === 'visible') void refresh();
        };
        const handleOnline = () => void refresh();
        window.addEventListener('focus', handleFocus);
        window.addEventListener('online', handleOnline);
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('online', handleOnline);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [refresh, tick]);

    useEffect(() => {
        const serverNowMs = useTradingSessionStore
            .getState()
            .getServerNowMs(clockNowMs);
        const delayMs = getTradingSessionTickerDelay(
            status,
            serverNowMs,
            document.visibilityState === 'visible',
        );
        if (delayMs === null) return;
        const timer = window.setTimeout(() => tick(), delayMs);
        return () => window.clearTimeout(timer);
    }, [clockNowMs, status, tick]);

    // An OAuth authority ends with a provider token the server keeps renewing
    // behind our back, and nothing else would ever re-read it: without this the
    // copy in the store simply rots until the next focus event, and every
    // mutation in between is told to open a trading window it does not need.
    useEffect(() => {
        const serverNowMs = useTradingSessionStore
            .getState()
            .getServerNowMs(clockNowMs);
        const delayMs = getOAuthAuthorityRefreshDelay(status, serverNowMs);
        if (delayMs === null) return;
        const timer = window.setTimeout(() => void refresh(), delayMs);
        return () => window.clearTimeout(timer);
    }, [clockNowMs, refresh, status]);

    useEffect(() => {
        const handleMessage = (message: TradingSessionBroadcastMessage) => {
            if (message.type === 'logout') {
                clearLocalAuthentication();
            } else if (message.type === 'oauth-authority-cleared') {
                clearLocalOAuthTradingAuthority();
            } else if (message.type === 'changed') {
                useWalletStore.getState().reloadSession();
                void refresh();
            }
        };
        const handleLocalMessage = (event: Event) => {
            const message = (
                event as CustomEvent<TradingSessionBroadcastMessage>
            ).detail;
            if (message) handleMessage(message);
        };
        window.addEventListener(
            TRADING_SESSION_LOCAL_EVENT,
            handleLocalMessage,
        );

        let channel: BroadcastChannel | null = null;
        if (typeof BroadcastChannel !== 'undefined') {
            channel = new BroadcastChannel(TRADING_SESSION_CHANNEL);
            channel.onmessage = (
                event: MessageEvent<TradingSessionBroadcastMessage>,
            ) => {
                if (event.data) handleMessage(event.data);
            };
        }
        return () => {
            window.removeEventListener(
                TRADING_SESSION_LOCAL_EVENT,
                handleLocalMessage,
            );
            channel?.close();
        };
    }, [refresh]);

    // the expiry warning asks for a V1 trading window: never in V2.
    const remainingMs =
        !browserWalletFlow &&
        (status?.method === 'eip712' || status?.method === 'passkey')
            ? useTradingSessionStore.getState().getRemainingMs(clockNowMs)
            : null;

    return (
        <>
            {children}
            {remainingMs !== null && (
                <TradingSessionWarning
                    remainingMs={remainingMs}
                    onReauthenticate={() =>
                        requestReauthentication(
                            remainingMs <= 0 ? 'expired' : 'expiring',
                        )
                    }
                />
            )}
            <TradingSessionDialog />
        </>
    );
};

export default TradingSessionProvider;
